import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "store";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { ImageUpload } from "components/imageUpload";
import { getSpinsList } from "store/liveOps/bonuses/slice";
import rewardIcon from "assets/icons/reward-icon.png";

import {
  getBonusOptions,
  getOriginalBonus,
  handleUpdateBonusMaskFields,
  handleSetSelectedBonusId,
} from "store/liveOps/promocodes/slice";
import { getGamesListService } from "services/gamesService";
import { handleApiError } from "helpers/handleApiError";

interface PromocodesMaskTabProps {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  selectedCurrencies: string[];
  onTargetBonusChange: (bonusId: number) => void;
  readOnly?: boolean;
}

const PromocodesMaskTab: React.FC<PromocodesMaskTabProps> = ({
  selectedCurrencies,
  onTargetBonusChange,
  fieldErrors,
  clearFieldError,
  readOnly
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const bonusMask = useAppSelector((store) => store.promocodes.bonusMask);
  const bonusOptions = useAppSelector((store) => store.promocodes.bonusOptions);
  const originalBonus = useAppSelector(
    (store) => store.promocodes.originalBonus,
  );
  const selectedBonusId = useAppSelector(
    (store) => store.promocodes.selectedBonusId,
  );
  const bonusMaskId = useAppSelector((store) => store.promocodes.bonusMask?.id);
  const { spinsOptions, isSpinsLoading, isSpinsError } = useAppSelector(
    (state) => state.bonuses,
  );
  const hasDepositPercentage = originalBonus?.rewards?.some(
    (reward) => reward.type === "depositPercentage",
  );
  const hasDepositStatic = originalBonus?.rewards?.some(
    (reward) => reward.type === "depositStatic",
  );
  const hasFreeSpins = originalBonus?.rewards?.some(
    (reward) => reward.type === "freespins",
  );
  const depositPercentageReward = originalBonus?.rewards?.find(
    (reward) => reward.type === "depositPercentage",
  );
  const depositStaticReward = originalBonus?.rewards?.find(
    (reward) => reward.type === "depositStatic",
  );
  const freeSpinsReward = originalBonus?.rewards?.find(
    (reward) => reward.type === "freespins",
  );

  const isBonusOptionsLoading = useAppSelector(
    (store) => store.promocodes.isBonusOptionsLoading,
  );
  const [search, setSearch] = useState("");
  const [searchGame, setSearchGame] = useState("");

  const [gamesOptions, setGamesOptions] = useState<
    { value: string; label: string; img?: string }[]
  >([]);

  const [loadingGames, setLoadingGames] = useState(false);

  const handleData = (
    key: string,
    value: string | number | boolean,
    img?: File | null,
  ) => {
    clearFieldError(key);
    dispatch(
      handleUpdateBonusMaskFields({
        [key]: img ?? value,
      }),
    );
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(getBonusOptions(search || undefined));
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [dispatch, search]);

  useEffect(() => {
    if (selectedBonusId) {
      dispatch(getOriginalBonus(selectedBonusId));
    }
  }, [selectedBonusId, dispatch]);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      setLoadingGames(true);

      try {
        const res = await getGamesListService({
          isActive: 1,
          hasFreeSpins: 1,
          gameTitle: searchGame || undefined,
        });

        const mapped = res.map((el: any) => ({
          value: String(el.id),
          label: el.title,
          img: el.image,
        }));

        setGamesOptions(mapped);
      } finally {
        setLoadingGames(false);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchGame]);

  useEffect(() => {
    if (!bonusMask?.freeSpinsGameId) {
      return;
    }

    dispatch(
      getSpinsList({
        gameId: String(bonusMask.freeSpinsGameId),
        currency: selectedCurrencies[0],
      }),
    )
      .unwrap()
      .catch((error) => {
        handleApiError({
          error,
          t,
          dispatch,
        });
      });
  }, [bonusMask?.freeSpinsGameId, selectedCurrencies, dispatch]);

  const targetBonusOptions = React.useMemo(() => {
    if (search.trim()) {
      return bonusOptions;
    }

    if (!originalBonus?.id || !originalBonus?.title) {
      return bonusOptions;
    }

    const exists = bonusOptions.some(
      (option) => String(option.value) === String(originalBonus.id),
    );

    if (exists) {
      return bonusOptions;
    }

    return [
      {
        value: originalBonus.id,
        label: originalBonus.title,
        img: originalBonus.image,
      },
      ...bonusOptions,
    ];
  }, [bonusOptions, originalBonus, search]);

  const selectedGame =
    bonusMask?.freeSpinsGameId && bonusMask?.freeSpinsGameTitle
      ? {
          value: String(bonusMask.freeSpinsGameId),
          label: bonusMask.freeSpinsGameTitle,
          img: bonusMask.freeSpinsGameImage,
        }
      : null;

  const finalGamesOptions = searchGame
    ? gamesOptions
    : selectedGame &&
        !gamesOptions.some((game) => game.value === selectedGame.value)
      ? [selectedGame, ...gamesOptions]
      : gamesOptions;

  return (
    <>
      <div className="bonus-form-content">
        <div className="bonus-form-box">
          <div className="bonus-form-box--item target-bonus">
            <DefaultDropdown
              value={selectedBonusId}
              label={t("targetBonus")}
              placeholder={t("selectBonus")}
              options={targetBonusOptions}
              loading={isBonusOptionsLoading}
              search={search}
              onSearch={setSearch}
              onSelect={(e) => {
                const bonusId = Number(e);

                dispatch(handleSetSelectedBonusId(bonusId));
                onTargetBonusChange(bonusId);

                handleData("targetBonusId", bonusId);
              }}
              disabled={readOnly}
            />
          </div>

          <div className="bonus-form-box--item">
            <div className="bonus-form-box--group">
              <div className="label">{t("title")}</div>

              <div className="bonus-form-box--row">
                <InnerInput
                  value={originalBonus?.title ?? ""}
                  label="Original"
                  type="text"
                  id="original-title"
                  disabled
                  onChange={() => {}}
                />

                <InnerInput
                  value={bonusMask?.title ?? ""}
                  label="Mask"
                  type="text"
                  id="mask-title"
                  onChange={(e) => handleData("title", e)}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          <div className="bonus-form-box--item">
            <InnerInput
              maxCodePoints={2500}
              value={bonusMask?.description ?? ""}
              placeholder={t("description")}
              label={t("description")}
              type="textarea"
              id="mask-description"
              onChange={(e) => handleData("description", e)}
              disabled={readOnly}
            />
          </div>
        </div>

        <div className="bonus-form-box">
          <div className="bonus-form-img promocode-mask-assets">
            <div className="bonus-images">
              <div className="bonus-image-item desktop">
                <ImageUpload
                  label="Desktop"
                  labelClassName="promocode-mask-image-label"
                  image={bonusMask?.image || originalBonus?.image}
                  size="large"
                  onAddImage={(file) => handleData("image", "", file)}
                  isActive
                  disabled={readOnly}
                />
              </div>

              <div className="bonus-image-item mobile">
                <ImageUpload
                  label="Mobile"
                  labelClassName="promocode-mask-image-label"
                  image={bonusMask?.mobileImage || originalBonus?.mobileImage}
                  size="large"
                  onAddImage={(file) => handleData("mobileImage", "", file)}
                  isActive
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>
          <div className="bonus-form-box--item bonus-form-box--row bonus-form-box--row-mask">
            <div className="bonus-form-box--group">
              <div className="label">Payout Multiplier</div>

              <div className="bonus-form-box--row">
                <InnerInput
                  value={originalBonus?.payoutMultiplier?.toString() ?? ""}
                  label="Original"
                  type="number"
                  id="original-payout"
                  onChange={() => {}}
                  disabled
                />

                <InnerInput
                  hasError={Boolean(fieldErrors?.payoutMultiplier)}
                  value={bonusMask?.payoutMultiplier?.toString() ?? ""}
                  label="Mask"
                  type="number"
                  id="mask-payout"
                  onChange={(e) => handleData("payoutMultiplier", Number(e))}
                  disabled={readOnly}
                />
              </div>
            </div>

            <div className="bonus-form-box--group">
              <div className="label">Wager (x)</div>

              <div className="bonus-form-box--row">
                <InnerInput
                  value={originalBonus?.wagerMultiplier?.toString() ?? ""}
                  label="Original"
                  type="number"
                  id="original-wager"
                  onChange={() => {}}
                  disabled
                />

                <InnerInput
                  hasError={Boolean(fieldErrors?.wagerMultiplier)}
                  value={bonusMask?.wagerMultiplier?.toString() ?? ""}
                  label="Mask"
                  type="number"
                  id="mask-wager"
                  onChange={(e) => handleData("wagerMultiplier", Number(e))}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bonus-form-content--settings">
        <div className="bonus-rewards-wrapper">
          <div className="bonus-rewards-list">
            {hasDepositPercentage && (
              <div className="bonus-rewards-list--item deposit-percentage">
                <div className="left">
                  <img src={rewardIcon} alt="" />
                  <div className="title">{t("depositPercentage")}</div>
                </div>
                <div className="right">
                  <div className="bonus-form-box--group">
                    <div className="label">{t("value")}</div>
                    <div className="inputs-row">
                      <div className="field">
                        <InnerInput
                          value={depositPercentageReward?.config?.value ?? ""}
                          label={t("originValue")}
                          id="freespins-count-original"
                          type="number"
                          disabled
                          onChange={() => {}}
                        />
                      </div>

                      <div className="field">
                        <InnerInput
                          hasError={Boolean(fieldErrors?.depositPercentageValue)}
                          value={bonusMask?.depositPercentageValue ?? ""}
                          label={t("mask")}
                          id="freespins-count-mask"
                          type="number"
                          onChange={(e) =>
                            handleData("depositPercentageValue", e)
                          }
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bonus-form-box--group">
                    <div className="label">{t("maxValue")}</div>
                    <div className="inputs-row">
                      <div className="field">
                        <InnerInput
                          value={depositPercentageReward?.config?.max ?? ""}
                          label={t("originValue")}
                          id="freespins-maxwin-original"
                          type="number"
                          disabled
                          onChange={() => {}}
                        />
                      </div>

                      <div className="field">
                        <InnerInput
                          hasError={Boolean(fieldErrors?.depositPercentageMax)}
                          value={bonusMask?.depositPercentageMax ?? ""}
                          label={t("mask")}
                          id="freespins-maxwin-mask"
                          type="number"
                          onChange={(e) =>
                            handleData("depositPercentageMax", e)
                          }
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {hasDepositStatic && (
              <div className="bonus-rewards-list--item deposit-static">
                <div className="left">
                  <img src={rewardIcon} alt="" />
                  <div className="title">{t("depositStatic")}</div>
                </div>

                <div className="right">
                  <div className="bonus-form-box--group">
                    <div className="label">{t("amount")}</div>

                    <div className="inputs-row">
                      <div className="field">
                        <InnerInput
                          value={depositStaticReward?.config?.value ?? ""}
                          label={t("originValue")}
                          id="deposit-static-original"
                          type="number"
                          disabled
                          onChange={() => {}}
                        />
                      </div>

                      <div className="field">
                        <InnerInput
                          hasError={Boolean(fieldErrors?.depositStaticAmount)}
                          value={bonusMask?.depositStaticAmount ?? ""}
                          label={t("mask")}
                          id="deposit-static-mask"
                          type="number"
                          onChange={(e) => handleData("depositStaticAmount", e)}
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {hasFreeSpins && (
              <div className="bonus-rewards-list--item free-spins">
                <div className="left">
                  <img src={rewardIcon} alt="" />
                  <div className="title">{t("freespins")}</div>
                </div>

                <div className="right">
                  <div className="bonus-form-box--group">
                    <div className="label">{t("game")}</div>

                    <div className="inputs-row inputs-row--game">
                      <div className="field">
                        <InnerInput
                          value={freeSpinsReward?.config?.gameTitle ?? ""}
                          label={t("originValue")}
                          id="freespins-game-original"
                          type="text"
                          disabled
                          onChange={() => {}}
                        />
                      </div>

                      <div className="field">
                        <DefaultDropdown
                          placeholder={t("chooseGame")}
                          label={t("mask")}
                          options={finalGamesOptions}
                          value={bonusMask?.freeSpinsGameId || ""}
                          loading={loadingGames}
                          search={searchGame}
                          onSearch={setSearchGame}
                          onSelect={(value) => {
                            handleData("freeSpinsGameId", Number(value));
                          }}
                          isTop
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bonus-form-box--group">
                    <div className="label">{t("spinAmount")}</div>

                    <div className="inputs-row">
                      <div className="field field--origin">
                        <InnerInput
                          value={freeSpinsReward?.config?.spinAmount ?? ""}
                          label={t("originValue")}
                          id="freespins-spin-original"
                          type="text"
                          disabled
                          onChange={() => {}}
                        />
                      </div>

                      <div className="field">
                        <DefaultDropdown
                          label={t("mask")}
                          disabled={isSpinsError || readOnly}
                          placeholder={
                            isSpinsError ? t("notAvailable") : t("spinAmount")
                          }
                          options={spinsOptions}
                          value={bonusMask?.freeSpinsSpinAmount ?? ""}
                          onSelect={(value) =>
                            handleData("freeSpinsSpinAmount", value as string)
                          }
                          isTop
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bonus-form-box--group">
                    <div className="label">{t("count")}</div>

                    <div className="inputs-row">
                      <div className="field">
                        <InnerInput
                          value={freeSpinsReward?.config?.totalAmount ?? ""}
                          label={t("originValue")}
                          id="freespins-count-original"
                          type="number"
                          disabled
                          onChange={() => {}}
                        />
                      </div>

                      <div className="field">
                        <InnerInput
                          hasError={Boolean(fieldErrors?.freeSpinsTotalAmount)}
                          value={
                            bonusMask?.freeSpinsTotalAmount?.toString() ?? ""
                          }
                          disabled={isSpinsError || readOnly}
                          label={t("mask")}
                          id="freespins-count-mask"
                          type="number"
                          onChange={(e) =>
                            handleData("freeSpinsTotalAmount", e)
                          }
                          placeholder={isSpinsError ? t("notAvailable") : ""}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bonus-form-box--group">
                    <div className="label">{t("maxWin")}</div>

                    <div className="inputs-row">
                      <div className="field">
                        <InnerInput
                          value={freeSpinsReward?.config?.maxWin ?? ""}
                          label={t("originValue")}
                          id="freespins-maxwin-original"
                          type="number"
                          disabled
                          onChange={() => {}}
                        />
                      </div>

                      <div className="field">
                        <InnerInput
                          hasError={Boolean(fieldErrors?.freeSpinsMaxWin)}
                          value={bonusMask?.freeSpinsMaxWin ?? ""}
                          disabled={isSpinsError || readOnly}
                          placeholder={isSpinsError ? t("notAvailable") : ""}
                          label={t("mask")}
                          id="freespins-maxwin-mask"
                          type="number"
                          onChange={(e) => handleData("freeSpinsMaxWin", e)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PromocodesMaskTab;
